<script setup lang="ts">
import { computed, onMounted, onUpdated, ref, watch } from 'vue';
import { useMutationObserver } from '@vueuse/core';
import { useElementSize } from '@vueuse/core';

const props = withDefaults(
  defineProps<{
    modelValue?: boolean;
    separator?: boolean;
    toggle?: boolean;
    static?: boolean;
    overflowVisible?: boolean;
    headerBackground?: 'white' | 'transparent' | 'none';
    headerBottomRadius?: boolean;
  }>(),
  {
    modelValue: false,
    separator: false,
    toggle: false,
    static: true,
    overflowVisible: false,
    headerBackground: 'none',
    headerBottomRadius: false,
  },
);
const emit = defineEmits(['update:modelValue', 'click']);

const el = ref<HTMLElement>();
const panel = ref<HTMLElement>();
const isOpen = ref(props.modelValue);
const height = ref('0');
const showContent = ref(false);
let timeout: ReturnType<typeof setTimeout>;

/** Applies a measured content height. A 0px reading is ignored so an open panel cannot snap shut. */
const applyOpenHeight = (measured?: number) => {
  if (!isOpen.value) return;
  const contentHeight = panel.value?.scrollHeight ?? 0;
  const next = measured && measured > 0 ? measured : contentHeight;
  if (next > 0) {
    height.value = `${next}px`;
  }
};

const updateState = (val?: boolean) => {
  const willOpen = val ?? !isOpen.value;
  clearTimeout(timeout);
  if (willOpen) {
    showContent.value = willOpen;
    timeout = setTimeout(() => {
      isOpen.value = willOpen;
      // auto covers a 0px measurement. Scrolling here jumps the row under the fixed header.
      height.value = 'auto';
      applyOpenHeight();
    }, 50);
  } else {
    isOpen.value = willOpen;
    timeout = setTimeout(() => {
      showContent.value = willOpen;
    }, 500);
    if (el.value) {
      el.value.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
};

const togglePanel = (val?: boolean) => {
  emit('update:modelValue', val ?? !isOpen.value);
  updateState(val);
};
const staticClass = ref(false);
const expClasses = computed(() => [
  {
    'exp-panel--open': isOpen.value,
    'exp-panel--separator': props.separator,
    'exp-panel--toggle': props.toggle,
    'exp-panel--static': staticClass.value,
    'exp-panel--allow-overflow': props.overflowVisible,
    [`exp-panel--header-bg-${props.headerBackground}`]: props.headerBackground !== 'none',
    'exp-panel--header-bottom-radius': props.headerBottomRadius,
  },
]);

watch(
  () => props.modelValue,
  (val) => {
    updateState(val);
  },
  { immediate: true },
);
watch(
  () => props.static,
  (val) => {
    setTimeout(
      () => {
        staticClass.value = val;
      },
      val ? 10 : 100,
    );
  },
);

onUpdated(() => {
  applyOpenHeight();
});

onMounted(() => {
  applyOpenHeight();
});
const { height: panelHeight } = useElementSize(panel);
watch([panelHeight, isOpen], ([measured, open]) => {
  if (open) {
    applyOpenHeight(measured);
  }
});

useMutationObserver(
  panel,
  () => {
    applyOpenHeight();
  },
  {
    attributes: true,
    childList: true,
    characterData: true,
    subtree: true,
  },
);
</script>

<template>
  <div ref="el" class="exp-panel" :class="expClasses">
    <div class="exp-panel__header" @click="toggle && togglePanel()">
      <slot name="header" :is-open="isOpen" :toggle="togglePanel" />
    </div>
    <div class="exp-panel__body">
      <div ref="panel" class="exp-panel__content" :style="{ display: showContent ? undefined : 'none' }">
        <slot name="body" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.exp-panel {
  --border-radius: 0.5rem;
  --border-color: #ccc;

  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);

  background-color: #fff;

  &__header {
    border-radius: var(--border-radius);

    overflow: hidden;
  }

  &__body {
    height: 0;

    border-radius: 0 0 var(--border-radius) var(--border-radius);

    overflow: hidden;

    transition: none;
  }

  &--toggle &__header {
    cursor: pointer;
  }

  &--separator &__body {
    box-shadow: inset 0 1px 0 0 var(--border-color);
  }

  &--static &__body {
    transition: none;
  }

  &--open {
    .exp-panel__header {
      @apply bg-gray-200;
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
    }

    .exp-panel__body {
      height: v-bind('height');

      transition: height 0.3s ease-out;
    }
  }

  &--allow-overflow {
    .exp-panel__header {
      overflow: visible;
    }
  }

  &--header-bg-white > &__header {
    background-color: #fff;
  }

  &--header-bg-transparent > &__header {
    background-color: transparent;
  }

  &--header-bottom-radius > &__header {
    border-bottom-right-radius: var(--border-radius);
    border-bottom-left-radius: var(--border-radius);
  }
}
</style>
